import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Country } from '@shared/domain/common/country';
import { COD_PAYMENT } from '@shared/domain/orders/constants';
import { CreateOrderInfo } from '@shared/domain/orders/create-order-info';
import { DeliveryEntity } from '@shared/domain/orders/delivery.entity';
import { DeliverySettingsModel } from '@shared/domain/orders/delivery.model';
import { PaymentModel } from '@shared/domain/orders/payment.model';
import { cloneDeep } from 'lodash';
import { CartService } from 'retail/app/common/providers/cart.service';
import { CheckoutService } from 'retail/app/orders/providers/checkout.service';
import { OrdersService } from 'retail/app/orders/providers/orders.service';
import { RetailAnalyticsService } from 'retail/app/retail-analytics.service';
import { forkJoin } from 'rxjs';
import { BalikovnaPickup } from 'shared-ui/providers/balikovna-pickup';
import { PplPickup } from 'shared-ui/providers/ppl-pickup';
import { ProgressService } from 'shared-ui/providers/progress.service';
import { ZasilkovnaPickup } from 'shared-ui/providers/zasilkovna-pickup';

@Component({
  selector: 'retail-shipment-payment',
  templateUrl: './shipment-payment.component.html',
  styleUrls: ['./shipment-payment.component.scss'],
})
export class ShipmentPaymentComponent implements OnInit {
  delivery?: DeliveryEntity;
  deliveries: DeliverySettingsModel[] = [];
  payment?: PaymentModel;
  payments: PaymentModel[] = [];
  submitted = false;
  pickup?: Partial<CreateOrderInfo>;
  weight = 0;
  @Output() back = new EventEmitter();
  @Output() next = new EventEmitter();

  constructor(
    private cartService: CartService,
    private checkoutService: CheckoutService,
    private ordersService: OrdersService,
    private progressService: ProgressService,
    private analyticsService: RetailAnalyticsService,
    private zasilkovnaPickup: ZasilkovnaPickup,
    private balikovnaPickup: BalikovnaPickup,
    private pplPickup: PplPickup
  ) {
    this.weight = CartService.getItemsWeight(this.cartService.cartItems);
  }

  ngOnInit(): void {
    this.loadSettings();
  }

  continue() {
    this.submitted = true;
    if (!!this.delivery && !!this.payment && this.ordersService.validateOrderDelivery()) {
      this.next.emit();
    }
  }

  chooseDelivery(delivery: DeliverySettingsModel) {
    if (delivery.id === 'ZASILKOVNA_PICKUP') {
      this.zasilkovnaPick(cloneDeep(delivery));
    } else if (delivery.id === 'BALIKOVNA_PICKUP') {
      this.balikovnaPick(cloneDeep(delivery));
    } else if (delivery.id === 'PPL_PICKUP') {
      this.pplPick(cloneDeep(delivery));
    } else {
      this.setDelivery(cloneDeep(delivery));
    }
  }

  choosePayment(payment: PaymentModel) {
    this.payment = payment;
    const order = this.ordersService.patchOrder('payment', this.payment);
    this.analyticsService.logCheckoutPayment(order);
  }

  private setDelivery(delivery: DeliverySettingsModel, isPickup = false, info?: Partial<CreateOrderInfo>) {
    this.pickup = info;
    this.delivery = this.getDelivery(delivery);
    this.ordersService.patchOrder('delivery', this.delivery);
    this.ordersService.patchOrder('isPickup', isPickup);
    const order = this.ordersService.patchOrder('info', (info ?? {}) as CreateOrderInfo);
    this.analyticsService.logCheckoutShipping(order);
    this.payment = undefined;
    this.ordersService.clearPayment();
    this.onDeliveryChange(delivery);
  }

  private loadSettings() {
    this.progressService.pageLoadStart();
    forkJoin([this.checkoutService.getDeliveries(this.weight), this.checkoutService.getPayments()]).subscribe(
      ([deliveries, payments]) => {
        this.deliveries = deliveries.filter(item => item.enabled && item.country === Country.CZ);
        this.payments = payments;
        this.loadOrder();
        this.progressService.pageLoadEnd();
      }
    );
  }

  private loadOrder() {
    const order = this.ordersService.loadOrder();
    this.payment = order.payment;
    if (order.delivery) {
      this.delivery = order.delivery;
      this.onDeliveryChange(this.getDeliverySettings(this.delivery));
      if (order.info?.delivery) {
        this.pickup = order.info;
      }
    } else {
      this.delivery = this.getDelivery(this.deliveries[0]);
      this.onDeliveryChange(this.deliveries[0]);
    }
    this.analyticsService.logCheckoutBegin(order);
  }

  private onDeliveryChange(delivery: DeliverySettingsModel) {
    delivery.payments.forEach(payment => {
      if (payment.id === COD_PAYMENT.id) {
        payment.price = delivery.codPrice;
      }
    });
    this.payments = delivery.payments;
    this.ordersService.patchOrder('delivery', this.getDelivery(delivery));
  }

  private getDeliverySettings({ id, country }: DeliveryEntity): DeliverySettingsModel {
    return this.deliveries.find(item => item.id === id && item.country === country)!;
  }

  private getDelivery(model: DeliverySettingsModel): DeliveryEntity {
    return DeliveryEntity.createFromData({
      id: model.id,
      country: model.country,
      title: model.title,
      price: model.price,
    });
  }

  private zasilkovnaPick(delivery: DeliverySettingsModel) {
    this.zasilkovnaPickup.pick({ weight: this.weight }).subscribe(point => {
      if (point) {
        this.setDelivery(delivery, true, {
          delivery: true,
          deliveryCity: point.city,
          deliveryCountry: point.country.toUpperCase() as Country,
          deliveryName: point.name,
          deliveryPostalcode: point.zip,
          deliveryStreet: point.street,
        });
      }
    });
  }

  private balikovnaPick(delivery: DeliverySettingsModel) {
    this.balikovnaPickup.pick().subscribe(point => {
      if (point) {
        this.setDelivery(delivery, true, {
          delivery: true,
          deliveryCity: point.municipality_name,
          deliveryCountry: point.country.toUpperCase() as Country,
          deliveryName: point.name,
          deliveryPostalcode: point.zip,
          deliveryStreet: point.street,
        });
      }
    });
  }

  private pplPick(delivery: DeliverySettingsModel) {
    this.pplPickup.pick().subscribe(point => {
      if (point) {
        this.setDelivery(delivery, true, {
          delivery: true,
          deliveryCity: point.city,
          deliveryCountry: point.country.toUpperCase() as Country,
          deliveryName: point.name,
          deliveryPostalcode: point.zipCode,
          deliveryStreet: point.street,
        });
      }
    });
  }
}
